/**
 * PORT: the trademark notice (the owner's request: Worms belongs to Team17; a notice where everybody sees it).
 *  - at every start the page's own loading screen shows the short notice under its status line (index.html #notice,
 *    src/main.ts): a returning player's session is restored and the main map follows it at once, and a new player's
 *    sign-in screen replaced the game's splash before it was drawn;
 *  - the sign-in screen (ui/auth-screen.ts) shows it at the bottom of its picture, clear of the form's frame;
 *  - the loading splash (FWP.loading_screen) shows the short notice at its bottom: a line or a few of
 *    small light text on a dark translucent band, measuring about TEXT_CSS CSS px on any screen (never under MIN_PX
 *    stage px), clear of the splash's progress bar, status line and advice: across the shown width where nothing is
 *    in the way, else in the widest free span of the bottom beside the progress bar (on a phone it reaches into the
 *    wide view's margin beside the window: the notice is not clipped to the window like the splash's own parts). It
 *    follows the shown area (Event.RESIZE: a rotation, a resize);
 *  - the settings screen's "Account and language" dialog has "About" (ABOUT_NAME), which opens the game's message
 *    popup (menu_error, mode ABOUT_MODE) with the game's name and the full notice.
 * The wording is the owner's (README §20, docs/google-play.md "Торговельні марки"): the same in the store listing, the
 * README and the privacy page. Only the name of the mark and its owner are given; no logo of theirs is used.
 */
import { Event, Point, Shape, Sprite, TextField, TextFormat } from '@flash';
import { translate } from '../runtime/i18n';
import { label, setRawText } from './controls';

/** The short notice (the loading splash) and what the full one adds (the About popup). Source strings: ru. */
export const MSG_TRADEMARK = 'Worms™ — товарный знак Team17 Digital Ltd. FWP не аффилирована с Worms и Team17.';
export const MSG_OTHER_MARKS = 'Все остальные товарные знаки принадлежат их владельцам.';
/** The About button of the account dialog. */
export const MSG_ABOUT = 'О проекте';
export const LEGAL_TEXTS = [MSG_TRADEMARK, MSG_OTHER_MARKS, MSG_ABOUT];

/** The account dialog's About button and the message popup's mode it opens. */
export const ABOUT_NAME = 'account_about';
export const ABOUT_MODE = 'about';
/** The game's name: the About popup's first line (a name: never translated; its version is in the main screen's corner). */
export const ABOUT_HEADING = 'FWP — Flash Wormage Project';

/** The full notice in the current language. */
export function legalNoticeFull(): string {
  return `${translate(MSG_TRADEMARK)} ${translate(MSG_OTHER_MARKS)}`;
}

/** The About popup (new_interf.render of menu_error in ABOUT_MODE): the name, bold, then the full notice. */
export function renderAbout(link: any): void {
  const tf: TextField = link.error_text;
  if (!tf) return;
  const text = `${ABOUT_HEADING}\n${legalNoticeFull()}`;
  if ((tf as any).$about === text) return;
  setRawText(tf, text);
  const bold = new TextFormat();
  bold.bold = true;
  tf.setTextFormat(bold, 0, ABOUT_HEADING.length);
  (tf as any).$about = text;
}

/** The splash's text: about TEXT_CSS CSS px, between MIN_PX and MAX_PX stage px; at most MAX_LINES lines. */
const TEXT_CSS = 10;
const MIN_PX = 9;
const MAX_PX = 22;
const MAX_LINES = 3;
/** Room round the band (to the shown area's edges and to what it keeps clear of) and inside it (CSS px). */
const MARGIN_CSS = 6;
const PAD_CSS = 3;
const TEXT_COLOR = 0xf2f2f2;
const BAND = { color: 0x000000, alpha: 0.55 };
/** The window (stage px). */
const WINDOW = { width: 807, height: 600 };

interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

function overlaps(a: Box, b: Box): boolean {
  return a.x < b.x + b.width && b.x < a.x + a.width && a.y < b.y + b.height && b.y < a.y + a.height;
}

/**
 * The notice of a loading splash (see SplashNotice), or null where the display runtime has no text fields to draw it
 * with (the minimal Flash stand-in the transpiler's unit tests boot the game root with).
 */
export function addSplashNotice(splash: any, keep: () => any[]): SplashNotice | null {
  if (typeof TextField !== 'function' || typeof Shape !== 'function') return null;
  return new SplashNotice(splash, keep);
}

/** The short notice at the bottom of a loading splash (see the module comment). */
export class SplashNotice {
  readonly root = new Sprite();
  readonly band = new Shape();
  readonly text: TextField;
  private key = '';

  /** `keep`: the splash's parts the notice keeps clear of (its progress bar, status line, advice). */
  constructor(
    private readonly splash: any,
    private readonly keep: () => any[],
  ) {
    this.root.name = '$legal_notice';
    this.root.mouseEnabled = false;
    this.root.mouseChildren = false;
    this.band.name = '$legal_notice_band';
    this.root.addChild(this.band);
    this.text = label(MSG_TRADEMARK, { size: 10, color: TEXT_COLOR, align: 'center', width: 300, lines: MAX_LINES });
    this.text.name = 'legal_notice';
    this.root.addChild(this.text);
    splash.addChild(this.root);
    this.root.addEventListener(Event.ADDED_TO_STAGE, this.onStage);
    this.root.addEventListener(Event.REMOVED_FROM_STAGE, this.offStage);
    if (this.root.stage) this.onStage();
  }

  /** Lays the notice out for the area the screen shows now (again only when it changed). */
  layout(force = false): void {
    const st: any = this.root.stage;
    if (!st) return;
    const css = st.cssScale > 0 ? st.cssScale : 1;
    const safe = this.local(st.safeRect);
    const keep = this.keepBoxes();
    const key = `${css}|${safe.x},${safe.y},${safe.width},${safe.height}|${keep.map((b) => `${Math.round(b.x)},${Math.round(b.y)},${Math.round(b.width)},${Math.round(b.height)}`).join(';')}|${this.text.text}`;
    if (!force && key === this.key) return;
    this.key = key;
    const m = MARGIN_CSS / css;
    const pad = PAD_CSS / css;
    const left = safe.x + m;
    const right = safe.x + safe.width - m;
    const bottom = safe.y + safe.height - m;
    const px = Math.max(MIN_PX, Math.min(MAX_PX, Math.round(TEXT_CSS / css)));
    // (the band and its text for the span l..r at the bottom; clear: it keeps clear of every part it must)
    const trial = (l: number, r: number) => {
      const f = this.fit(r - l - 2 * pad, px, pad);
      const w = Math.min(r - l, f.w + 2 * pad);
      const box = { x: (l + r) / 2 - w / 2 - m / 2, y: bottom - f.h - m / 2, width: w + m, height: f.h + m };
      return { l, r, clear: !keep.some((k) => overlaps(k, box)), top: bottom - f.h };
    };
    // Across the shown width where nothing is in the way (a portrait or a tall screen: under the window's picture),
    // else the widest free span of the bottom beside the progress bar that holds it clear.
    const full = trial(left, right);
    let pick = full;
    if (!full.clear) {
      const spans = this.spans(left, right, full.top, bottom, keep, m).sort((a, b) => b.r - b.l - (a.r - a.l));
      const tried = spans.map((sp) => trial(sp.l, sp.r));
      pick = tried.find((t) => t.clear) ?? tried[0] ?? full;
    }
    const f = this.fit(pick.r - pick.l - 2 * pad, px, pad);
    this.place(pick.l, pick.r, bottom - f.h, f, pad);
  }

  /** The text at `px` (smaller while it takes more than MAX_LINES lines) in `width`; the band's height. */
  private fit(width: number, px: number, pad: number): { px: number; w: number; h: number } {
    const tf = this.text;
    tf.width = Math.max(40, width);
    let size = px;
    for (;;) {
      const f = new TextFormat();
      f.size = size;
      tf.setTextFormat(f);
      if (tf.numLines <= MAX_LINES || size <= MIN_PX) break;
      size -= 1;
    }
    tf.height = tf.textHeight + 4;
    return { px: size, w: tf.textWidth + 4, h: tf.height + 2 * pad };
  }

  /** The band and the text centred in the span `l`..`r` from `top`. */
  private place(l: number, r: number, top: number, f: { w: number; h: number }, pad: number): void {
    const w = Math.min(r - l, f.w + 2 * pad);
    const x = Math.round((l + r) / 2 - w / 2);
    const y = Math.round(top);
    const g = this.band.graphics;
    g.clear();
    g.beginFill(BAND.color, BAND.alpha);
    g.drawRoundRect(x, y, Math.round(w), Math.round(f.h), 8 * pad, 8 * pad);
    g.endFill();
    this.text.x = Math.round(x + (w - this.text.width) / 2);
    this.text.y = Math.round(y + pad);
  }

  /** The free spans of the bottom row `top`..`bottom` between the boxes reaching into it. */
  private spans(left: number, right: number, top: number, bottom: number, keep: Box[], m: number): Array<{ l: number; r: number }> {
    const cuts = keep.filter((b) => b.y + b.height > top - m && b.y < bottom).map((b) => ({ l: b.x - m, r: b.x + b.width + m }));
    cuts.sort((a, b) => a.l - b.l);
    const out: Array<{ l: number; r: number }> = [];
    let x = left;
    for (const c of cuts) {
      if (c.l > x) out.push({ l: x, r: Math.min(c.l, right) });
      x = Math.max(x, c.r);
    }
    if (x < right) out.push({ l: x, r: right });
    return out.filter((s) => s.r - s.l > 40);
  }

  private keepBoxes(): Box[] {
    const out: Box[] = [];
    for (const o of this.keep()) {
      if (!o || !o.visible) continue;
      const b = o.getBounds(this.splash);
      if (b.width > 0 && b.height > 0) out.push({ x: b.x, y: b.y, width: b.width, height: b.height });
    }
    return out;
  }

  /** A stage rectangle in the splash's coordinates. */
  private local(r: Box): Box {
    const p0 = this.splash.globalToLocal(new Point(r.x, r.y));
    const p1 = this.splash.globalToLocal(new Point(r.x + r.width, r.y + r.height));
    return { x: Math.min(p0.x, p1.x), y: Math.min(p0.y, p1.y), width: Math.abs(p1.x - p0.x), height: Math.abs(p1.y - p0.y) };
  }

  private stageRef: any = null;
  private readonly onStage = (): void => {
    const st = this.root.stage;
    if (!st || this.stageRef === st) return;
    this.stageRef = st;
    st.addEventListener(Event.RESIZE, this.onResize);
    this.root.addEventListener(Event.ENTER_FRAME, this.onFrame);
    this.layout(true);
  };
  private readonly offStage = (): void => {
    if (this.stageRef) this.stageRef.removeEventListener(Event.RESIZE, this.onResize);
    this.stageRef = null;
    this.root.removeEventListener(Event.ENTER_FRAME, this.onFrame);
  };
  private readonly onResize = (): void => this.layout(true);
  /** (cheap while nothing changed: the key of the shown area and of the parts kept clear of) */
  private readonly onFrame = (): void => {
    // (the splash's children are clipped to the window once it is dressed for a wide screen, ui/wide-screen.ts: the
    // notice may lie in the margin beside the window)
    if ((this.root as any).$designClip) (this.root as any).$designClip = false;
    this.layout();
  };
}
